import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { createCart, addToShopifyCart } from "@/lib/shopify";

const CartContext = createContext(null);
const CART_ID_KEY = "ybc_cart_id";

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [shopifyCartId, setShopifyCartId] = useState(() => localStorage.getItem(CART_ID_KEY));
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Restore cart ID from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(CART_ID_KEY);
    if (stored) setShopifyCartId(stored);
  }, []);

  const addItem = useCallback(async (product) => {
    // Update local state immediately for snappy UI
    setItems((prev) => {
      const key = product.variantId || product.id;
      const existing = prev.find((i) => (i.variantId || i.id) === key);
      if (existing) {
        return prev.map((i) =>
          (i.variantId || i.id) === key ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsOpen(true);

    // Show toast notification
    toast.success(`${product.title} added to bag`, {
      description: product.size ? `Size: ${product.size}` : undefined,
      duration: 2500,
    });

    // Sync with Shopify Cart API if we have a variantId
    if (product.variantId) {
      setLoading(true);
      try {
        if (shopifyCartId) {
          const result = await addToShopifyCart(shopifyCartId, product.variantId, 1);
          if (result?.cart?.checkoutUrl) {
            setCheckoutUrl(result.cart.checkoutUrl);
          }
        } else {
          const result = await createCart(product.variantId, 1);
          if (result?.cart?.id) {
            setShopifyCartId(result.cart.id);
            localStorage.setItem(CART_ID_KEY, result.cart.id);
            if (result.cart.checkoutUrl) {
              setCheckoutUrl(result.cart.checkoutUrl);
            }
          }
        }
      } catch (err) {
        console.error("Shopify cart sync failed:", err);
      } finally {
        setLoading(false);
      }
    }
  }, [shopifyCartId]);

  const removeItem = useCallback((id) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) {
        toast(`${item.title} removed from bag`, { duration: 2000 });
      }
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.id !== id));
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity } : i))
    );
  }, []);

  const checkout = useCallback(() => {
    if (checkoutUrl) {
      window.open(checkoutUrl, "_blank");
    } else {
      toast.error("Checkout unavailable. Please try again.");
    }
  }, [checkoutUrl]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        setIsOpen,
        addItem,
        removeItem,
        updateQuantity,
        checkout,
        checkoutUrl,
        totalItems,
        totalPrice,
        loading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
